import React from "react";

interface UseAutoPlayOptions {
    enabled: boolean;
    interval: number;
    onAdvance: () => void;
    progressRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Manages an autoplay timer for a carousel. When `progressRef` is provided,
 * uses requestAnimationFrame to track elapsed time and sets a
 * `--autoplay-progress` CSS custom property (0–1) on the element.
 * Otherwise, uses a simple setInterval.
 *
 * Returns `resetTimer` which resets elapsed time to 0 (call on manual nav).
 */
export function useAutoPlay({
    enabled,
    interval,
    onAdvance,
    progressRef,
}: UseAutoPlayOptions): { resetTimer: () => void } {
    // Store onAdvance in a ref so the timer callback never goes stale
    const onAdvanceRef = React.useRef(onAdvance);
    React.useEffect(() => {
        onAdvanceRef.current = onAdvance;
    });

    const elapsedRef = React.useRef(0);
    const lastTimeRef = React.useRef(0);

    React.useEffect(() => {
        if (!enabled) {
            // Reset lastTime so paused duration isn't counted as elapsed
            // when we resume. elapsedRef is kept so the progress bar freezes
            // in place rather than snapping to zero.
            lastTimeRef.current = 0;
            return;
        }

        if (progressRef) {
            // rAF-based loop with CSS custom property progress
            let rafId: number;

            const tick = (timestamp: number) => {
                if (lastTimeRef.current === 0) {
                    lastTimeRef.current = timestamp;
                }
                const delta = timestamp - lastTimeRef.current;
                lastTimeRef.current = timestamp;
                elapsedRef.current += delta;

                const progress = Math.min(elapsedRef.current / interval, 1);
                progressRef.current?.style.setProperty(
                    "--autoplay-progress",
                    String(progress),
                );

                if (elapsedRef.current >= interval) {
                    onAdvanceRef.current();
                    elapsedRef.current = 0;
                }

                rafId = requestAnimationFrame(tick);
            };

            rafId = requestAnimationFrame(tick);
            return () => cancelAnimationFrame(rafId);
        } else {
            // Simple setInterval (no progress tracking needed)
            const id = setInterval(() => {
                onAdvanceRef.current();
            }, interval);
            return () => clearInterval(id);
        }
    }, [enabled, interval, progressRef]);

    // useCallback keeps a stable identity so consumers can safely include
    // resetTimer in effect dependency arrays without causing re-runs.
    const resetTimer = React.useCallback(() => {
        elapsedRef.current = 0;
        lastTimeRef.current = 0;
        progressRef?.current?.style.setProperty("--autoplay-progress", "0");
    }, [progressRef]);

    return { resetTimer };
}
